// petz-search.js — U1-RP1 search experience
// Facets, sort, mobile drawer, banner, dept filter, predictive search.
// No jQuery, no framework. AbortController + debounce.

(function () {
  'use strict';

  // ─── Constants ─────────────────────────────────────────────────────────────

  const ALLOWED_DEPTS = new Set(['dogs', 'cats', 'bird', 'small-pet']);

  // Departments submit Shopify's own Product-metafield filter parameter — the
  // same native contract the main Search results page reads.
  const DEPT_FILTER_PREFIX = 'filter.p.m.custom.pet_department_';
  const DEPT_FILTER_VALUE = '1';

  function deptFilterName(dept) {
    return DEPT_FILTER_PREFIX + String(dept).replace(/-/g, '_');
  }

  const DEBOUNCE_MS = 300;
  // Shopify matches short prefixes broadly. Requiring three meaningful
  // characters avoids noisy one- and two-letter predictive results while the
  // native GET search remains available whenever the form is submitted.
  const MIN_QUERY = 3;
  const PREDICTIVE_LIMIT = 6;
  const PREDICTIVE_TYPES = 'query,product,collection';
  const PREDICTIVE_FIELDS = [
    'title',
    'product_type',
    'variants.title',
    'vendor',
    'variants.sku',
    'variants.barcode',
  ].join(',');

  // ─── Utilities ─────────────────────────────────────────────────────────────

  function debounce(fn, ms) {
    let t;
    function debounced(...a) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, a), ms);
    }
    debounced.cancel = function () {
      clearTimeout(t);
      t = null;
    };
    return debounced;
  }




  // ─── Header Dept (non-search pages) ────────────────────────────────────────

  class PetzHeaderDept {
    constructor() {
      // The main Search results page renders its department controls as native
      // Shopify filter links, so it exposes no dept buttons for this to manage.
      if (document.querySelector('[data-petz-main-search]')) return;
      this.btns = document.querySelectorAll('[data-petz-dept-btn]');
      this.inputs = document.querySelectorAll('[data-petz-dept-input]');
      this.btns.forEach(btn => {
        btn.addEventListener('click', () => {
          const val = btn.dataset.petzDeptValue || '';
          this._onDept(ALLOWED_DEPTS.has(val) ? val : '');
        });
      });
    }

    _onDept(dept) {
      this.btns.forEach(btn => {
        const val = btn.dataset.petzDeptValue || '';
        const active = ALLOWED_DEPTS.has(val) ? val : '';
        btn.setAttribute('aria-pressed', String(active === dept));
      });
      this.inputs.forEach(inp => {
        if (dept && ALLOWED_DEPTS.has(dept)) {
          inp.setAttribute('name', deptFilterName(dept));
          inp.value = DEPT_FILTER_VALUE;
        } else {
          inp.removeAttribute('name');
          inp.value = '';
        }
      });
    }
  }

  // ─── Predictive Search ─────────────────────────────────────────────────────

  class PetzPredictiveSearch {
    static _isSupported() {
      try {
        const el = document.querySelector('#shopify-features');
        if (!el) return false;
        const features = JSON.parse(el.textContent);
        return features.predictiveSearch === true;
      } catch {
        return false;
      }
    }

    constructor(form) {
      if (!PetzPredictiveSearch._isSupported()) return;

      this.form = form;
      this.input = form.querySelector('input[name="q"]');

      // Resolve dropdown by aria-controls ID — not DOM nesting.
      // Each rendered input has a unique aria-controls value that points to exactly
      // one predictive results element, wherever it sits in the DOM.
      const ariaControlsId = this.input ? this.input.getAttribute('aria-controls') : null;
      const byId = ariaControlsId ? document.getElementById(ariaControlsId) : null;
      this.dropdown = (byId && byId.hasAttribute('data-petz-predictive-dropdown')) ? byId : null;

      this.baseUrl = form.dataset.petzPredictiveUrl || '/search/suggest';
      this.abort = null;

      if (!this.input || !this.dropdown) return;

      this._fetch = debounce(this._doFetch.bind(this), DEBOUNCE_MS);

      this.input.addEventListener('input', () => {
        const q = this.input.value.trim();
        // Never leave results from the previous term visible while the next
        // native predictive request is settling.
        this._close();
        if (q.length >= MIN_QUERY) this._fetch(q);
      });

      this.input.addEventListener('focus', () => {
        const q = this.input.value.trim();
        if (q.length >= MIN_QUERY && this.dropdown.hidden) this._fetch(q);
      });

      this.input.addEventListener('keydown', e => {
        if (e.key === 'Escape') {
          this._close();
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          const first = this.dropdown.querySelector('[role="option"]');
          if (first) first.focus();
          return;
        }
        if (e.key === 'Tab') {
          this._close();
        }
      });

      this.dropdown.addEventListener('keydown', e => {
        const options = Array.from(this.dropdown.querySelectorAll('[role="option"]'));
        const idx = options.indexOf(document.activeElement);
        if (e.key === 'Escape') {
          this._close();
          this.input.focus();
          e.preventDefault();
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (idx < options.length - 1) options[idx + 1].focus();
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (idx > 0) options[idx - 1].focus();
          else this.input.focus();
          return;
        }
        if (e.key === 'Tab') {
          this._close();
        }
      });

      document.addEventListener('click', e => {
        // Close only when click is outside BOTH the form and the resolved dropdown.
        // The dropdown may live outside the <form> subtree (header context).
        if (!this.form.contains(e.target) && !this.dropdown.contains(e.target)) {
          this._close();
        }
      });
    }

    async _doFetch(q) {
      if (this.abort) this.abort.abort();
      this.abort = new AbortController();
      try {
        const url =
          `${this.baseUrl}?q=${encodeURIComponent(q)}` +
          `&resources[type]=${encodeURIComponent(PREDICTIVE_TYPES)}` +
          `&resources[limit]=${PREDICTIVE_LIMIT}` +
          `&resources[options][fields]=${encodeURIComponent(PREDICTIVE_FIELDS)}` +
          `&resources[options][unavailable_products]=last` +
          `&section_id=predictive-search`;
        const res = await fetch(url, {
          headers: { Accept: 'text/html' },
          signal: this.abort.signal,
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const text = await res.text();
        const tmp = document.createElement('div');
        tmp.innerHTML = text;
        const section = tmp.querySelector('#shopify-section-predictive-search');
        const content = (section ? section.innerHTML : text).trim();
        if (content) {
          this.dropdown.innerHTML = content;
          this.dropdown.hidden = false;
          this.input.setAttribute('aria-expanded', 'true');
        } else {
          this._close();
        }
      } catch (err) {
        if (err.name !== 'AbortError') this._close();
      }
    }

    _close() {
      this._fetch.cancel();
      this.dropdown.hidden = true;
      this.dropdown.innerHTML = '';
      if (this.input) this.input.setAttribute('aria-expanded', 'false');
      if (this.abort) { this.abort.abort(); this.abort = null; }
    }
  }

  // ─── Global Liquid card variant controller ──────────────────────────────────
  // Handles in-place variant state updates for Liquid-rendered product cards
  // (product-card.liquid). Each visible button carries full state as data attrs.

  function initLiquidCardVariants() {
    document.addEventListener('click', function(e) {
      const btn = e.target.closest('[data-petz-card-variant]');
      if (!btn) return;
      e.preventDefault();
      const card = btn.closest('[data-petz-product-card]');
      if (!card) return;

      const variantUrl = btn.dataset.variantUrl || '#';
      const variantImage = btn.dataset.variantImage || '';
      const priceStr = btn.dataset.variantPrice || '';
      const compareFmt = btn.dataset.variantCompareFmt || '';
      const discountPct = parseInt(btn.dataset.variantDiscountPct || '0', 10);
      const discountText = btn.dataset.variantDiscountText || '';
      const available = btn.dataset.variantAvailable !== 'false';
      const hasSub = btn.dataset.variantHasSub === 'true';

      card.querySelectorAll('[data-petz-card-variant]').forEach(function(b) {
        const isActive = b === btn;
        b.classList.toggle('petz-product-card__option-btn--active', isActive);
        b.setAttribute('aria-pressed', String(isActive));
      });

      card.querySelectorAll('[data-petz-card-link]').forEach(function(a) {
        a.href = variantUrl;
      });

      if (variantImage) {
        const imgEl = card.querySelector('.petz-product-card__media img');
        if (imgEl && imgEl.src !== variantImage) imgEl.src = variantImage;
      }

      const priceEl = card.querySelector('[data-petz-price]');
      if (priceEl && priceStr) priceEl.textContent = priceStr;

      const compareEl = card.querySelector('[data-petz-compare-price]');
      if (compareEl) {
        if (compareFmt) {
          compareEl.textContent = compareFmt;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      const discountEl = card.querySelector('[data-petz-discount-badge]');
      if (discountEl) {
        if (discountPct > 0) {
          discountEl.textContent = discountText;
          discountEl.hidden = false;
        } else {
          discountEl.textContent = '';
          discountEl.hidden = true;
        }
      }

      const oosEl = card.querySelector('[data-petz-oos-badge]');
      if (oosEl) oosEl.hidden = available;

      const subEl = card.querySelector('[data-petz-sub-signal]');
      if (subEl) subEl.hidden = !hasSub;

      const badgesEl = card.querySelector('[data-petz-badges]');
      if (badgesEl) {
        const saleOptEl = card.querySelector('[data-petz-sale-options-badge]');
        const hasSaleOpt = saleOptEl && !saleOptEl.hidden;
        badgesEl.hidden = discountPct <= 0 && available && !hasSaleOpt;
      }
    });
  }

  // ─── Init ──────────────────────────────────────────────────────────────────

  function init() {
    initLiquidCardVariants();

    new PetzHeaderDept();

    document.querySelectorAll('[data-petz-predictive-url]').forEach(form => {
      new PetzPredictiveSearch(form);
    });
  }

  document.addEventListener('shopify:section:load', e => {
    const s = e.target;
    s.querySelectorAll('[data-petz-predictive-url]').forEach(form => new PetzPredictiveSearch(form));
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
